import { useEffect, useState } from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import { cn } from "@/cn";
import {
  applyTheme,
  cacheThemePreference,
  nextThemePreference,
  readCachedThemePreference,
  subscribeToSystemTheme,
  themePreferenceLabel,
  type ThemePreference,
} from "@/lib/theme";

export function ThemeToggle({ className }: { className?: string }) {
  const [preference, setPreference] = useState<ThemePreference>("system");

  useEffect(() => {
    const initial = readCachedThemePreference();
    setPreference(initial);
    applyTheme(initial);
  }, []);

  useEffect(() => {
    if (preference !== "system") return;
    return subscribeToSystemTheme(() => applyTheme("system"));
  }, [preference]);

  function cycle() {
    const next = nextThemePreference(preference);
    setPreference(next);
    cacheThemePreference(next);
    applyTheme(next);
  }

  const Icon =
    preference === "system" ? Monitor : preference === "dark" ? Sun : Moon;
  const label = themePreferenceLabel(preference);
  const nextLabel = themePreferenceLabel(nextThemePreference(preference));

  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={`Theme: ${label}. Switch to ${nextLabel}`}
      title={`Theme: ${label} (click for ${nextLabel})`}
      className={cn(
        "inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground max-sm:min-h-[44px] max-sm:min-w-[44px]",
        className,
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}
